import { useEffect, useMemo, useState } from 'react';
import ErrorDisplay from '../../../../components/ErrorDisplay';
import { CommunityQuestionPicker } from '../../../../pickers/community-question/CommunityQuestionPicker';
import type { CommunityQuestionBase } from '../../../Community/types/community-questions';
import type { QuestTemplateCommunityQuestionFirestore, QuestTemplateFirestore } from '../../services/questTypes';
import { buildResourceUsageMap } from '../../utils/questUsageUtils';
import { QuestFormActions } from './QuestFormActions';
import { QuestFormFields } from './QuestFormFields';
import { QuestFormSectionDivider } from './QuestFormSectionDivider';
import { useQuestForm } from './useQuestForm';

interface CommunityQuestionSpecificFormData {
    questionId: string;
}

interface CommunityQuestionQuestFormProps {
    questTemplate?: QuestTemplateCommunityQuestionFirestore;
    initialOrder: number;
    onSave: (questTemplate: QuestTemplateCommunityQuestionFirestore) => void;
    onSaveAndContinue?: (questTemplate: QuestTemplateCommunityQuestionFirestore) => void;
    onCancel: () => void;
    allQuestTemplates: QuestTemplateFirestore[];
}

export const CommunityQuestionQuestForm = ({ questTemplate, initialOrder, onSave, onSaveAndContinue, onCancel, allQuestTemplates }: CommunityQuestionQuestFormProps) => {
    const {
        commonFormData,
        loading,
        error,
        handleCommonChange,
        handleSubmit,
        handleSaveAndContinue
    } = useQuestForm({
        questTemplate,
        initialOrder,
        defaultProgressType: 'fixed'
    });

    const [specificFormData, setSpecificFormData] = useState<CommunityQuestionSpecificFormData>({
        questionId: '',
    });

    // Build usage map for community questions
    const questionUsageMap = useMemo(() => {
        return buildResourceUsageMap(allQuestTemplates, 'questionId');
    }, [allQuestTemplates]);

    useEffect(() => {
        if (questTemplate && questTemplate.id) {
            setSpecificFormData({
                questionId: questTemplate.questionId,
            });
        }
    }, [questTemplate]);

    const handleSpecificChange = (field: string, value: string | boolean) => {
        setSpecificFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleChange = (field: string, value: string | boolean) => {
        if (field in commonFormData) {
            handleCommonChange(field as keyof typeof commonFormData, value as string | boolean);
        } else {
            handleSpecificChange(field, value);
        }
    };

    const validateSpecificFields = (): string | null => {
        if (!specificFormData.questionId.trim()) {
            return 'Question ID is required for community question quests';
        }
        return null;
    };

    const createQuestTemplate = (baseData: Partial<QuestTemplateCommunityQuestionFirestore>): QuestTemplateCommunityQuestionFirestore => {
        return {
            ...baseData,
            type: 'community-question',
            maxProgress: 1,
            questionId: specificFormData.questionId,
        } as QuestTemplateCommunityQuestionFirestore;
    };

    const onSubmit = handleSubmit(validateSpecificFields, createQuestTemplate, onSave);
    const onSaveAndContinueClick = onSaveAndContinue ? handleSaveAndContinue(validateSpecificFields, createQuestTemplate, onSaveAndContinue) : undefined;

    return (
        <form onSubmit={onSubmit} className="space-y-4">
            {error && (
                <ErrorDisplay message={error} />
            )}

            <QuestFormFields
                formData={commonFormData}
                questType="community-question"
                disableProgressType={true}
                onChange={handleChange}
            />
            <QuestFormSectionDivider title="Community Question-specific fields" />

            <CommunityQuestionPicker
                value={specificFormData.questionId}
                onChange={(question: CommunityQuestionBase | null) => {
                    if (question) {
                        setSpecificFormData(prev => ({
                            ...prev,
                            questionId: question.id,
                        }));
                    } else {
                        setSpecificFormData(prev => ({ ...prev, questionId: '' }));
                    }
                }}
                usageMap={questionUsageMap}
            />

            {error && (
                <ErrorDisplay message={error} />
            )}

            <QuestFormActions
                onCancel={onCancel}
                onSaveAndContinue={onSaveAndContinueClick}
                loading={loading}
                isEditMode={!!questTemplate?.id}
            />
        </form>
    );
};
